import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';
import { Button, Modal, TextLinkify, theme, useResponsive } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const AccountDeletionSecondStep = (props: SecondStepProps) => {
  const { body, isDeletingAccount, requestAccountDeletion } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();
  const { top: safeAreaTop, bottom: safeAreaBottom } = useSafeAreaInsets();

  const containerStyles = useStyle(
    () => ({
      paddingTop: Platform.select({ android: safeAreaTop, default: 0 }),
      paddingBottom: Platform.select({ android: safeAreaBottom, default: 0 }),
    }),
    [safeAreaTop, safeAreaBottom],
  );

  return (
    <View style={containerStyles}>
      <Modal.Headline>
        {formatMessage(messages.accountDeleteHeadline)}
      </Modal.Headline>

      <Modal.BodyText>
        <TextLinkify>{body}</TextLinkify>
      </Modal.BodyText>

      <Modal.Footer withoutTopBorder style={styles.footer}>
        <Button
          testID="account.delete.confirm"
          size={match(['medium', 'large'])}
          style={styles.deleteAccountCTA}
          isLoading={isDeletingAccount}
          onPress={requestAccountDeletion}
        >
          {formatMessage(messages.accountDeleteConfirm)}
        </Button>
      </Modal.Footer>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountDeleteHeadline: {
    defaultMessage: 'Delete account',
    description: 'Account | Delete | Headline',
  },
  accountDeleteConfirm: {
    defaultMessage: 'Delete your data & account',
    description: 'Account | Delete | Confirm',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  deleteAccountCTA: {
    marginTop: theme.spacing['4'],
  },
  footer: {
    marginBottom: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type SecondStepProps = Readonly<{
  body: string;
  isDeletingAccount: boolean;
  requestAccountDeletion: () => void;
}>;
